import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './Profile.css';

// UAE Emirates list
const EMIRATES = [
  { id: 'abu_dhabi', key: 'abuDhabi' },
  { id: 'dubai', key: 'dubai' },
  { id: 'sharjah', key: 'sharjah' },
  { id: 'ajman', key: 'ajman' },
  { id: 'umm_al_quwain', key: 'umm_al_quwain' },
  { id: 'ras_al_khaimah', key: 'ras_al_khaimah' },
  { id: 'fujairah', key: 'fujairah' }
];

const Profile = () => {
  const { user, updateProfile, token } = useAuth();
  const { t, isRTL } = useLanguage();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // Address state
  const [address, setAddress] = useState({
    fullName: '',
    phone: '',
    address: '',
    city: '',
    emirate: '',
    country: 'UAE'
  });
  const [addressLoading, setAddressLoading] = useState(false);

  // Fetch saved address
  useEffect(() => {
    const fetchSavedAddress = async () => {
      try {
        const response = await fetch('/api/orders/saved-address', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const savedAddress = await response.json();
          if (savedAddress) {
            setAddress(savedAddress);
          }
        }
      } catch (error) {
        console.error('Failed to fetch saved address:', error);
      }
    };

    if (token) {
      fetchSavedAddress();
    }
  }, [token]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setLoading(true);

    try {
      await updateProfile({ name, email });
      setMessage({ type: 'success', text: t('profileUpdated') });
    } catch (err) {
      setMessage({ type: 'danger', text: err.message || t('updateFailed') });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'danger', text: t('passwordsDoNotMatch') });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'danger', text: t('passwordMinLength') });
      return;
    }

    setLoading(true);

    try {
      await updateProfile({ currentPassword, newPassword });
      setMessage({ type: 'success', text: t('passwordChanged') });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
    } catch (err) {
      setMessage({ type: 'danger', text: err.message || t('passwordChangeFailed') });
    } finally {
      setLoading(false);
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddress(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressSave = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setAddressLoading(true);

    try {
      const response = await fetch('/api/users/address', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ address })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('addressSaved') || 'Address saved successfully' });
      } else {
        const data = await response.json();
        setMessage({ type: 'danger', text: data.error || 'Failed to save address' });
      }
    } catch (err) {
      setMessage({ type: 'danger', text: 'Failed to save address' });
    } finally {
      setAddressLoading(false);
    }
  };

  return (
      <div className={`profile-page ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container">
        <div className="page-header">
          <h1>{t('myProfile')}</h1>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type}`}>{message.text}</div>
        )}

        <div className="profile-grid">
          <div className="profile-card">
            <h2>{t('profileInfo')}</h2>
            <form onSubmit={handleProfileUpdate}>
              <div className="form-group">
                <label className="form-label">{t('fullName')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('email')}</label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? t('saving') : t('saveChanges')}
              </button>
            </form>

            {/* Change Password Link */}
            <div className="password-section">
              {!showPasswordForm ? (
                <button
                  type="button"
                  className="change-password-link"
                  onClick={() => setShowPasswordForm(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg> {t('changePassword')}
                </button>
              ) : (
                <div className="password-form">
                  <div className="password-form-header">
                    <h3>{t('changePassword')}</h3>
                    <button
                      type="button"
                      className="close-btn"
                      onClick={() => setShowPasswordForm(false)}
                    >
                      ✕
                    </button>
                  </div>
                  <form onSubmit={handlePasswordChange}>
                    <div className="form-group">
                      <label className="form-label">{t('currentPassword')}</label>
                      <input
                        type="password"
                        className="form-input"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('newPassword')}</label>
                      <input
                        type="password"
                        className="form-input"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('confirmNewPassword')}</label>
                      <input
                        type="password"
                        className="form-input"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? t('changing') : t('changePassword')}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>

          {/* Shipping Address Card */}
          <div className="profile-card">
            <h2>{t('shippingAddress')}</h2>
            <form onSubmit={handleAddressSave}>
              <div className="form-group">
                <label className="form-label">{t('fullName')}</label>
                <input
                  type="text"
                  name="fullName"
                  className="form-input"
                  value={address.fullName}
                  onChange={handleAddressChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input input-ltr"
                  value={address.phone}
                  onChange={handleAddressChange}
                  placeholder="+971 XX XXX XXXX"
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('address')}</label>
                <input
                  type="text"
                  name="address"
                  className="form-input"
                  value={address.address}
                  onChange={handleAddressChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('city')}</label>
                  <input
                    type="text"
                    name="city"
                    className="form-input"
                    value={address.city}
                    onChange={handleAddressChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('emirate')}</label>
                  <select
                    name="emirate"
                    className="form-input"
                    value={address.emirate}
                    onChange={handleAddressChange}
                  >
                    <option value="">{t('selectEmirate')}</option>
                    {EMIRATES.map(emirate => (
                      <option key={emirate.id} value={emirate.id}>
                        {t(emirate.key)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={addressLoading}
              >
                {addressLoading ? t('saving') : t('saveChanges')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
